/**
 * General Ledger preview. The backend is the system of record (GET /api/accounting/gl/entries);
 * this builds the same double-entry journal from local state for demo mode and as a cross-check.
 *
 * Journal lines reference an account by its stable `id`, never by `code`, so the chart of accounts can be
 * renumbered later (GET/PUT /api/accounting/gl/accounts) without touching history.
 */
import { getPricing } from './rateEngine';
import { invoiceTitle, manifestDay } from './payroll';

/** Starter chart of accounts for a Quebec courier company - codes and names are editable in the GL screen. */
export const DEFAULT_ACCOUNTS = [
  { id: 'bank', code: '1000', name: 'Bank — Operating', type: 'Asset' },
  { id: 'petty_cash', code: '1010', name: 'Petty Cash', type: 'Asset' },
  { id: 'ar', code: '1200', name: 'Accounts Receivable', type: 'Asset' },
  { id: 'gst_itc', code: '1300', name: 'GST Receivable (ITC)', type: 'Asset' },
  { id: 'qst_itr', code: '1310', name: 'QST Receivable (ITR)', type: 'Asset' },
  { id: 'driver_advances', code: '1400', name: 'Driver Advances', type: 'Asset' },
  { id: 'ap', code: '2000', name: 'Accounts Payable', type: 'Liability' },
  { id: 'company_card', code: '2050', name: 'Company Credit Card', type: 'Liability' },
  { id: 'driver_payable', code: '2100', name: 'Driver Pay Payable', type: 'Liability' },
  { id: 'gst_payable', code: '2200', name: 'GST Payable', type: 'Liability' },
  { id: 'qst_payable', code: '2210', name: 'QST Payable', type: 'Liability' },
  { id: 'vacation_payable', code: '2320', name: 'Vacation Pay Payable (accrued)', type: 'Liability' },
  { id: 'payroll_clearing', code: '2330', name: 'Payroll Clearing — Accountant', type: 'Liability' },
  { id: 'owner_equity', code: '3000', name: "Owner's Equity", type: 'Equity' },
  { id: 'retained_earnings', code: '3100', name: 'Retained Earnings', type: 'Equity' },
  { id: 'delivery_revenue', code: '4000', name: 'Delivery Revenue', type: 'Revenue' },
  { id: 'accessorial_revenue', code: '4010', name: 'Accessorial Revenue', type: 'Revenue' },
  { id: 'freight_revenue', code: '4100', name: 'Freight Revenue (manual invoices)', type: 'Revenue' },
  { id: 'driver_chargebacks', code: '4900', name: 'Driver Chargebacks', type: 'Revenue' },
  { id: 'driver_pay', code: '5000', name: 'Driver Wages', type: 'Expense' },
  { id: 'vacation_expense', code: '5020', name: 'Vacation Pay Expense', type: 'Expense' },
  { id: 'owner_operator_fees', code: '5050', name: 'Owner-Operator Fees', type: 'Expense' },
  { id: 'exp_fuel', code: '6100', name: 'Fuel & Oil', type: 'Expense' },
  { id: 'exp_repairs', code: '6200', name: 'Repairs & Maintenance', type: 'Expense' },
  { id: 'exp_tires', code: '6210', name: 'Tires', type: 'Expense' },
  { id: 'exp_parts', code: '6220', name: 'Parts & Supplies', type: 'Expense' },
  { id: 'exp_tolls', code: '6300', name: 'Tolls & Highway Fees', type: 'Expense' },
  { id: 'exp_parking', code: '6310', name: 'Parking', type: 'Expense' },
  { id: 'exp_scale', code: '6320', name: 'Scale Fees', type: 'Expense' },
  { id: 'exp_lumper', code: '6330', name: 'Lumper Fees', type: 'Expense' },
  { id: 'exp_detention', code: '6340', name: 'Detention Fees', type: 'Expense' },
  { id: 'exp_permits', code: '6400', name: 'Permits, Licenses & Registration', type: 'Expense' },
  { id: 'exp_lease', code: '6500', name: 'Fleet Lease & Rental', type: 'Expense' },
  { id: 'exp_insurance', code: '6600', name: 'Insurance', type: 'Expense' },
  { id: 'exp_rent', code: '6700', name: 'Facility Rent', type: 'Expense' },
  { id: 'exp_software', code: '6800', name: 'Software & Telematics', type: 'Expense' },
  { id: 'exp_communication', code: '6810', name: 'Communication', type: 'Expense' },
  { id: 'exp_office', code: '6900', name: 'Office Supplies', type: 'Expense' },
  { id: 'exp_professional', code: '6910', name: 'Professional Services', type: 'Expense' },
  { id: 'exp_cleaning', code: '6920', name: 'Cleaning', type: 'Expense' },
  { id: 'exp_meals', code: '6930', name: 'Meals', type: 'Expense' },
  { id: 'exp_lodging', code: '6940', name: 'Lodging', type: 'Expense' },
  { id: 'exp_other', code: '6999', name: 'Other Expenses', type: 'Expense' },
];

export const ACCOUNT_TYPES = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'];

const TAX_ACCOUNTS = { GST: 'gst_payable', QST: 'qst_payable' };

const dr = (account, amount) => ({ account, debit: amount, credit: 0 });
const cr = (account, amount) => ({ account, debit: 0, credit: amount });
const nonZero = (lines) => lines.filter((l) => Math.abs(l.debit) > 0.004 || Math.abs(l.credit) > 0.004);
const reverse = (lines) => lines.map((l) => ({ account: l.account, debit: l.credit, credit: l.debit }));

const invoiceTaxLines = (inv) =>
  nonZero([dr('ar', inv.taxes.reduce((s, t) => s + t.amount, 0)), ...inv.taxes.map((t) => cr(TAX_ACCOUNTS[t.code] || 'gst_payable', t.amount))]);

export const buildJournal = ({ manifests, cards, settlements = [], invoices = [] }) => {
  const entries = [];

  manifests
    .filter((m) => m.status === 'posted')
    .forEach((m) => {
      const t = getPricing(m, cards).totals;
      entries.push({
        id: `JE-${m.id}`,
        date: (m.postedAt || m.date).slice(0, 10),
        source: 'manifest',
        ref: m.id,
        memo: `Manifest ${m.route} · ${t.pieces} pcs · ${m.driver?.name || 'Unassigned'} (${manifestDay(m)})`,
        lines: nonZero([
          dr('ar', t.customer),
          cr('delivery_revenue', t.base),
          cr('accessorial_revenue', t.accessorial),
          dr('driver_pay', t.driver),
          cr('driver_payable', t.driver),
        ]),
      });
    });

  // The TMS stops at gross pay: approval books adjustments + vacation accrual; "processed" hands the gross to the
  // accountant (payroll_clearing), whose final payroll entries (deductions, net pay, remittances) clear it.
  settlements.forEach((s) => {
    if (s.status === 'draft') return;
    const approvedOn = (s.approvedAt || s.periodStart).slice(0, 10);
    const bonuses = s.adjustments.filter((a) => Number(a.amount) > 0).reduce((x, a) => x + Number(a.amount), 0);
    const deductions = -s.adjustments.filter((a) => Number(a.amount) < 0).reduce((x, a) => x + Number(a.amount), 0);
    if (bonuses || deductions) {
      entries.push({
        id: `JE-${s.id}-ADJ`,
        date: approvedOn,
        source: 'settlement',
        ref: s.id,
        memo: `Pay adjustments · ${s.driver.name}`,
        lines: nonZero([dr('driver_pay', bonuses), cr('driver_payable', bonuses), dr('driver_payable', deductions), cr('driver_chargebacks', deductions)]),
      });
    }
    if (s.vacationAccrued) {
      entries.push({
        id: `JE-${s.id}-VAC`,
        date: approvedOn,
        source: 'settlement',
        ref: s.id,
        memo: `Vacation pay accrued ${+(s.vacationRate * 100).toFixed(2)}% · ${s.driver.name}`,
        lines: nonZero([dr('vacation_expense', s.vacationAccrued), cr('vacation_payable', s.vacationAccrued)]),
      });
    }
    if (s.status === 'processed') {
      entries.push({
        id: `JE-${s.id}-PROC`,
        date: (s.processedAt || s.periodStart).slice(0, 10),
        source: 'settlement',
        ref: s.id,
        memo: `Gross pay sent to accountant · ${s.driver.name}`,
        lines: nonZero([dr('driver_payable', s.grossPay), cr('payroll_clearing', s.grossPay)]),
      });
    }
  });

  // Revenue is booked when manifests post; invoices only add sales tax. A revised invoice reverses the previous one.
  invoices.forEach((inv) => {
    const label = `${inv.number}${inv.revision ? ` rev ${inv.revision}` : ''}`;
    entries.push({
      id: `JE-${label.replace(/\s/g, '-')}`,
      date: inv.issuedAt.slice(0, 10),
      source: 'invoice',
      ref: label,
      memo: `Sales tax on ${invoiceTitle(inv)} · ${inv.customer}`,
      lines: invoiceTaxLines(inv),
    });
    if (inv.status === 'superseded') {
      entries.push({
        id: `JE-${label.replace(/\s/g, '-')}-REV`,
        date: (inv.supersededAt || inv.issuedAt).slice(0, 10),
        source: 'invoice',
        ref: label,
        memo: `Reversal — invoice ${label} replaced by a revision`,
        lines: reverse(invoiceTaxLines(inv)),
      });
    }
  });

  return entries.sort((a, b) => b.date.localeCompare(a.date) || a.id.localeCompare(b.id));
};

export const accountIndex = (accounts) => Object.fromEntries(accounts.map((a) => [a.id, a]));

export const trialBalance = (entries, accounts) => {
  const idx = accountIndex(accounts);
  const totals = {};
  entries.forEach((e) =>
    e.lines.forEach((l) => {
      const row = totals[l.account] || { id: l.account, debit: 0, credit: 0 };
      row.debit += l.debit;
      row.credit += l.credit;
      totals[l.account] = row;
    })
  );
  return Object.values(totals)
    .map((r) => ({ ...r, ...(idx[r.id] || { code: '?', name: `Unknown account ${r.id}`, type: '—' }), balance: r.debit - r.credit }))
    .sort((a, b) => String(a.code).localeCompare(String(b.code)));
};
